/* eslint-disable camelcase */
import moment from "moment";
import AppContext from "PFApp/app_context";
import { usePage } from "PFApp/use_page";
import { getProfileName } from "PFCore/helpers/profile";
import { getVisibleProperties } from "PFCore/helpers/templates";
import { fetchAttachments } from "PFCore/services/attachments/fetch_attachments";
import { ApiRoute } from "PFCore/utilities/routes";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { shouldDisplayProperty } from "./utils/should_display_property";

export const useActivityEditPage = (initPage, { id, type, attributes, isClone }) => {
  // to avoid race condition page should be stored under different key on each mount
  const pageId = useState(Math.floor(Math.random() * 10000))[0];

  const { page, setPage, mergePage, resetPage } = usePage(`activity_edit_${pageId}`);
  const { activity, parentActivity, parentActivityReady } = page;

  const { currentProfile, customTypes } = useContext(AppContext).store;
  const { t } = useTranslation("activities");

  const fetchSubtemplates = (templateKey) =>
    new Promise((resolve) => {
      if (!templateKey) {
        return resolve([]);
      }
      $.ajax({
        url: ApiRoute(`/api/subtemplates?template_key=${templateKey}`),
        api_version: 2
      })
        .then(resolve)
        .fail(() => {
          resolve([]);
        });
    });

  const fetch = ({ id }) => {
    $.when(
      $.ajax({
        url: ApiRoute(`/api/activities/${id}`),
        api_version: 2
      }),
      fetchAttachments({ targetType: "task", targetId: id })
    ).then(
      (activityResp, attachmentsResp) => {
        const activityAttributes = activityResp[0];
        const attachments = attachmentsResp[0].entries;
        const templateId = activityAttributes.template_id;
        const template = currentProfile.templates.find(
          (template) => template.id === templateId && template.read_permitted
        );

        fetchSubtemplates(template.key).then((subtemplates) => {
          mergePage({
            template,
            subtemplates,
            attachments,
            activity: activityAttributes,
            loaded: true
          });
        });
      },
      () => {
        mergePage({
          loaded: true,
          errors: [t("translation:thereWasAnError")]
        });
      }
    );
  };

  useEffect(() => {
    setPage(initPage);
    return resetPage;
  }, []);

  useEffect(() => {
    if (id && currentProfile.templates && currentProfile.templates.length > 0) {
      fetch({ id });
    }
  }, [id, currentProfile.templates && currentProfile.templates.length]);

  const parentId = activity?.parent_activity_id || attributes?.parent_activity_id;
  useEffect(() => {
    if (parentId) {
      $.ajax({
        url: ApiRoute(`/api/activities/${parentId}`),
        api_version: 2
      }).then(
        (resp) => mergePage({ parentActivity: resp, parentActivityReady: true }),
        () => {
          if (attributes?.parent_activity_id) {
            attributes.parent_activity_id = null;
          }
          mergePage({ parentActivityReady: true });
        } // in case e.g. 403 or 404 is returned
      );
    } else {
      mergePage({ parentActivityReady: true });
    }
  }, [parentId]);

  // get template when only type is passed (new activity)
  useEffect(() => {
    const isNew = !id;
    const templates = currentProfile.templates || [];

    if (type && isNew && parentActivityReady && templates.length > 0) {
      const template = templates.find(
        (template) => template.key === type && !template.hidden && template.create_permitted
      );

      const activityAttributes = {
        custom_fields: [],
        metadata: {},
        ...attributes
      };

      if (!isClone && parentActivity?.assignee) {
        activityAttributes.assignee = parentActivity.assignee;
      }

      const loaded = !!customTypes;

      fetchSubtemplates(template?.key).then((subtemplates) => {
        // set defaults
        if (template && loaded) {
          activityAttributes.post_until = moment()
            .add(template.default_post_until_days, "days")
            .toISOString();

          const getParentCustomFieldValues = () => {
            if (parentActivity) {
              return parentActivity.custom_fields.reduce((obj, customField) => {
                if (template.schema.properties[customField.type.name]) {
                  obj[customField.type.name] = customField.values;
                }
                return obj;
              }, {});
            } else {
              return {};
            }
          };

          // custom defaults not from schema
          const customDefaultValues =
            {
              engagement: {
                requested_by: getProfileName(currentProfile),
                service_line: currentProfile.service_line
              },
              role: {
                ...getParentCustomFieldValues(),
                requested_by: getProfileName(currentProfile),
                talent_pool: currentProfile.talent_pool
              }
            }[template.key] || {};

          getVisibleProperties(template).forEach((property) => {
            const hasDefault = "default" in property || property.name in customDefaultValues;
            const isInAttributesSection =
              !property.match && shouldDisplayProperty(property, customTypes, currentProfile);
            const hasSubtemplates = subtemplates.length > 0;
            const allowFillingDefault = !(hasSubtemplates && isInAttributesSection);

            if (hasDefault && allowFillingDefault) {
              const defaultValue = property.default || customDefaultValues[property.name];
              if (property.type === "custom_field") {
                const customType = customTypes.filter((type) => type.name === property.name)[0];
                const valueBlank = !activityAttributes.custom_fields.find(
                  (field) => field.type.id === customType.id
                );
                if (customType && defaultValue && valueBlank) {
                  activityAttributes.custom_fields = [
                    ...activityAttributes.custom_fields,
                    {
                      type: { id: customType.id, name: customType.name },
                      values: defaultValue
                    }
                  ];
                }
              } else {
                const valueBlank = activityAttributes.metadata[property.name] === undefined;
                if (valueBlank) {
                  activityAttributes.metadata[property.name] = defaultValue;
                }
              }
            }
          });
        }

        mergePage({
          // no template => all done, let controller navigate
          loaded: (template ? parentActivityReady : true) && templates.length > 0,
          activity: activityAttributes,
          template,
          subtemplates
        });
      });
    }
  }, [id, type, currentProfile, customTypes, parentActivity, parentActivityReady]);

  return { ...page, fetch };
};
